import { Link as RouterLink, useParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { FALLBACK_PRODUCTS } from "../data/biomarkers";
import { FALLBACK_CARE_CATEGORIES } from "../data/site";
import { getProducts, getTreatmentCategories } from "../services/veysoApi";
import { useApiData } from "../hooks/useApiData";
import FallbackNotice from "../components/FallbackNotice";
import { Card, Cards, Eyebrow, PageHero, Section } from "../components/ui";
import { useCart } from "../components/CartContext";
import { tokens } from "../theme";

export default function Shop() {
  const { audience } = useParams();
  const aud = ["women", "men"].includes(audience) ? audience : null;
  const { add, count } = useCart();
  const { data, usingFallback } = useApiData(() => getProducts("ADD_ON"), FALLBACK_PRODUCTS.filter((p) => p.type === "ADD_ON"));
  const { data: goals } = useApiData(() => getTreatmentCategories(aud), FALLBACK_CARE_CATEGORIES.filter((c) => !aud || c.audience === aud), { deps: [aud] });
  const label = aud === "women" ? "Women's products" : aud === "men" ? "Men's products" : "All products";
  return (
    <>
      <PageHero theme="moss" eyebrow="Shop" title={label}
        subtitle="Sample catalog. Items go in a browser-only cart; checkout comes in a later phase." />
      <Section>
        <Eyebrow>Browse by goal</Eyebrow>
        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          {(goals || []).map((g) => (
            <Box key={g.slug} component={RouterLink} to={`/treatment/${g.slug}-visit`} sx={{ textDecoration: "none", color: tokens.ink, background: tokens.paper, border: `1px solid ${tokens.line}`, borderRadius: 999, px: 2.25, py: 1, fontSize: 15 }}>
              {g.name}{aud ? "" : ` (${g.audience})`}
            </Box>
          ))}
        </Box>
      </Section>
      <Section tone="white">
        <FallbackNotice show={usingFallback} />
        <Cards min={260}>
          {(data || []).map((a) => (
            <Card key={a.id} sx={{ background: tokens.cream, display: "flex", flexDirection: "column" }}>
              <Typography variant="h4" component={RouterLink} to={`/shop/p/${a.id}`} sx={{ fontSize: 21, color: tokens.ink, textDecoration: "none" }}>{a.name}</Typography>
              <Typography sx={{ color: tokens.muted, mt: 1, flex: 1 }}>{a.detail}</Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 3 }}>
                <Typography sx={{ fontWeight: 500, fontSize: 20 }}>${a.priceCents / 100}</Typography>
                <Button variant="contained" onClick={() => add({ id: a.id, name: a.name, price: a.priceCents / 100 })}>Add to cart</Button>
              </Box>
            </Card>
          ))}
        </Cards>
        {count > 0 && <Button sx={{ mt: 4 }} variant="outlined" component={RouterLink} to="/cart">View cart ({count})</Button>}
      </Section>
    </>
  );
}

export function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const { data, loading, usingFallback } = useApiData(() => getProducts(), FALLBACK_PRODUCTS);
  if (loading) return null;
  const p = (data || []).find((x) => String(x.id) === String(id));
  return (
    <>
      <PageHero theme="moss" eyebrow="Product" title={p ? p.name : "Product not found"} subtitle={p ? p.detail : "This product is not in the catalog."} />
      <Section tone="white" narrow>
        <FallbackNotice show={usingFallback} />
        {p && (
          <Card>
            <Typography sx={{ fontSize: 30, fontWeight: 300 }}>${p.priceCents / 100}</Typography>
            <Typography sx={{ color: tokens.muted, mt: 1 }}>Why we picked it, reviews and bundles will appear here once VEYSO supplies the copy. Sample page.</Typography>
            <Button sx={{ mt: 3 }} variant="contained" onClick={() => add({ id: p.id, name: p.name, price: p.priceCents / 100 })}>Add to cart</Button>
          </Card>
        )}
        <Button sx={{ mt: 3 }} component={RouterLink} to="/shop">Back to shop</Button>
      </Section>
    </>
  );
}
